import { Menu, LogOut, UserCog, ShoppingCart, Compass } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAccount } from "@/hooks/useAccount";
import { useCart } from "@/hooks/useCart";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

interface TopBarProps {
  onOpenMobileNav: () => void;
}

/**
 * Sticky header. Shows the live profile (name/email/role) in a dropdown with
 * a link to edit the profile and sign out. The cart is hidden for admins —
 * the operator view isn't a shopping surface.
 */
export function TopBar({ onOpenMobileNav }: TopBarProps) {
  const { email, logout } = useAuth();
  const { data: account } = useAccount();
  const { data: cart } = useCart();

  const isAdmin = account?.user_role === "Admin";
  const name = account?.user_full_name?.trim();
  const displayEmail = account?.user_email ?? email ?? "Account";
  const initial = (name || displayEmail || "U").slice(0, 1).toUpperCase();
  const cartCount = cart?.item_count ?? 0;

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="flex h-14 items-center gap-3 px-4 sm:h-16 sm:px-6">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onOpenMobileNav}
          aria-label="Open navigation"
        >
          <Menu className="h-5 w-5" />
        </Button>

        <div className="flex-1" />

        {/* Explore plans — customers only. Jumps to the marketing home's
            services section so they can browse/buy more. */}
        {!isAdmin && (
          <Button asChild variant="outline" size="sm" className="hidden sm:inline-flex">
            <a href="/#services">
              <Compass className="h-4 w-4" />
              Explore plans
            </a>
          </Button>
        )}

        {/* Cart — customers only */}
        {!isAdmin && (
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="relative"
            aria-label={`Cart${cartCount ? `, ${cartCount} items` : ""}`}
          >
            <Link to="/cart">
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-primary-foreground">
                  {cartCount > 9 ? "9+" : cartCount}
                </span>
              )}
            </Link>
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label="Open account menu"
            >
              <Avatar>
                <AvatarFallback>{initial}</AvatarFallback>
              </Avatar>
              <span className="hidden text-sm font-medium text-foreground sm:inline">
                {name || displayEmail}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="truncate font-semibold">{name || "My account"}</span>
              <span className="truncate text-xs font-normal text-muted-foreground">{displayEmail}</span>
              {account?.user_role && (
                <span className="mt-1 inline-flex w-fit rounded-full bg-brand-accentSoft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-accentDark">
                  {account.user_role}
                </span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!isAdmin && (
              <DropdownMenuItem asChild className="sm:hidden">
                <a href="/#services">
                  <Compass className="mr-2 h-4 w-4" />
                  Explore plans
                </a>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <Link to="/settings">
                <UserCog className="mr-2 h-4 w-4" />
                Edit profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void logout()}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
